import { ONE_MINUTE_MS } from '@eflight/shared';
import { API, graphqlOperation } from 'aws-amplify';
import type Dexie from 'dexie';
import { isEqual, omit } from 'lodash';

import { cognitoUserIsSignedIn } from '../../reducers/cognitoUserReducer';
import { type SyncState } from '../../reducers/syncReducer';
import { SYNC_CHANGE } from '../../reducers/syncReducer';
import { initReauthFlow } from '../../utils/Authentication/InitReauthFlow';
import { logError } from '../../utils/ErrorLog/ErrorLog';
import {
    getImpersonatedCognitoState,
    IMPERSONATION_READ_ONLY_MESSAGE,
    isImpersonating,
} from '../../utils/Impersonation/impersonation';
import { store } from '../../wrappers/WithStore';
import { whenOnline } from '../util/whenOnline';
import { getDb } from './LocalDB';
import {
    type DbSyncerUpCommand,
    type DbSyncSubscription,
    type DbSyncSubscriptionAdditionalData,
    type SyncableTable,
    type SyncItem,
} from './syncHelpers';
import { syncUpCommandsToMutation } from './syncHelpers';
import { dbSyncerResyncSubscriptions, dbSyncerUpdateSubscriptions, dbSyncerUpDefintions } from './syncSubscriptions';

type QueueItem = {
    subscription: DbSyncSubscription;
    nextToken?: string;
};

const LOCAL_STORAGE_KEY_LAST_SYNC = 'alol_synctime';
const LOCAL_STORAGE_KEY_LAST_SYNC_DEVICE = 'alol_synctime_device';
const LOCAL_STORAGE_KEY_DB_VERSION = 'alol_syncversion';
const LOCAL_DB_MAJOR_VERSION = '1';

class SyncRowMissingError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'SyncRowMissingError';
    }
}

// Amplify reports a failed request as a GraphQL error wrapping the axios error, not as a code.
const isNetworkError = (e: any): boolean =>
    e?.code === 'NetworkError' ||
    Boolean(
        e?.errors?.some((x: any) => x?.message === 'Network Error' || x?.originalError?.message === 'Network Error')
    );

// Amplify's API wraps any session it cannot get in an Error; only Auth itself rejects with the bare string.
const isNoCurrentUserError = (e: any): boolean => e?.message === 'No current user';

type CurrentSyncData = {
    startTime: number;
    promise: Promise<void>;
};
export class DbSyncer {
    initPromise: Promise<this>;
    lastSyncTime: number = parseInt(window.localStorage.getItem(LOCAL_STORAGE_KEY_LAST_SYNC) || '0', 10);
    // The same moment by this device's clock, which can disagree with the server's (lastSyncTime).
    lastSyncDeviceTime: number = parseInt(window.localStorage.getItem(LOCAL_STORAGE_KEY_LAST_SYNC_DEVICE) || '0', 10);
    timeout: number | null = null;
    syncInterval: number | null = null;
    currentSync: CurrentSyncData | null = null;
    // Set on sign-out: a sync still running must not write into the next user's database.
    dropped = false;

    constructor() {
        this.initPromise = this.init();
    }

    async init(): Promise<this> {
        if (window.localStorage.getItem(LOCAL_STORAGE_KEY_DB_VERSION) !== LOCAL_DB_MAJOR_VERSION) {
            await getDb().wipe();
            window.localStorage.setItem(LOCAL_STORAGE_KEY_DB_VERSION, LOCAL_DB_MAJOR_VERSION);
            window.localStorage.removeItem(LOCAL_STORAGE_KEY_LAST_SYNC);
            this.lastSyncTime = 0;
        }
        try {
            await this.sync();
        } catch (e) {
            console.error(e);
        }
        // a syncer dropped by a sign-out during its first sync must not keep syncing on its own
        if (dbSyncer === this) {
            this.syncInterval = window.setInterval(() => this.sync(), 30000);
        }
        return this;
    }

    async initComplete(): Promise<this> {
        return this.initPromise;
    }

    async markToSync(table: Dexie.Table, ...indices: string[]) {
        if (!indices.length) {
            return;
        }
        if (isImpersonating()) {
            // read-only mode: keep the queue clean so nothing is pushed under the borrowed identity
            console.error(`${IMPERSONATION_READ_ONLY_MESSAGE} (sync ${table.name})`);

            return;
        }
        const tableName = table.name as SyncableTable;
        if (!dbSyncerUpDefintions.has(tableName)) {
            throw new Error(`Trying to sync item with no definition: ${tableName}`);
        }
        const syncItems: SyncItem[] = indices.map((itemId): SyncItem => {
            const id = `${tableName}:${itemId}`;
            return { id, itemId, tableName };
        });
        await getDb().syncItems.bulkPut(syncItems);
        this.updateSyncState({ setLengthUp: await getDb().syncItems.count() });
        if (!this.timeout) {
            this.timeout = window.setTimeout(() => {
                this.timeout = null;
                return this.sync();
            }, 1000);
        }
    }

    updateSyncState(params: {
        isInProgress?: boolean;
        lengthUpIncrement?: number;
        setLengthUp?: number;
        setLengthDown?: number;
        offline?: boolean;
        // Time of the last successful download; the shown sync time moves only then.
        syncedAt?: number;
    }) {
        const { isInProgress, lengthUpIncrement, setLengthUp, setLengthDown } = params;
        const syncState = store.getState().sync;
        const queueLength =
            setLengthUp === undefined
                ? lengthUpIncrement === undefined
                    ? syncState.queueLengthUp
                    : syncState.queueLengthUp + lengthUpIncrement
                : setLengthUp;
        const inProgress = isInProgress === undefined ? syncState.inProgress : isInProgress;
        const queueLengthDown = setLengthDown === undefined ? syncState.queueLengthDown : setLengthDown;
        const time = params.syncedAt ?? syncState.time;
        const newState: SyncState = {
            inProgress,
            queueLengthUp: queueLength,
            queueLengthDown,
            time,
            offline: params.offline ?? syncState.offline,
        };
        store.dispatch({ type: SYNC_CHANGE, data: newState });
    }

    isStale(): boolean {
        return this.getLastSyncAge() > 30000;
    }

    shouldDoCompleteResync(): boolean {
        // set to 30 minutes to better prevent total uncontrolled resync
        return this.getLastSyncAge() > 30 * ONE_MINUTE_MS;
    }

    // Measured on the device clock alone. A reset last-sync time (0) is always too old, whatever the device time says.
    private getLastSyncAge(): number {
        return this.lastSyncTime > 0 ? Date.now() - this.lastSyncDeviceTime : Infinity;
    }

    async wipe() {
        this.setLastSyncTime(0, 0);
    }

    sync(force?: boolean): Promise<void> {
        const startTime = Date.now();
        // An offline sync only waits to go online, so a second one would just run the same upload alongside it.
        if (this.currentSync && (!navigator.onLine || startTime - this.currentSync.startTime < 29000)) {
            console.warn('Other sync in progress, aborting');
            return this.currentSync.promise;
        }
        this.currentSync = { startTime, promise: this.runSync(startTime, force) };
        return this.currentSync.promise;
    }

    // Never rejects: callers await a sync only to continue after it.
    private async runSync(startTime: number, force?: boolean): Promise<void> {
        if (this.timeout) {
            window.clearTimeout(this.timeout);
            this.timeout = null;
        }
        try {
            // noinspection PointlessBooleanExpressionJS - for when navigator doesn't support onLine property and it is udefined
            if (!navigator.onLine) {
                this.updateSyncState({ offline: true });
                await whenOnline();
                this.updateSyncState({ offline: false });
            }
            await this.syncUp();
            this.updateSyncState({ isInProgress: true });
            await this.syncDown(force);
        } catch (e) {
            // syncUp has logged it. Nothing is downloaded over what it could not send, which stays queued.
            console.error(e);
        } finally {
            this.updateSyncState({ isInProgress: false });
            // a sync that outlived the guard must not clear the marker of the one that replaced it
            if (this.currentSync?.startTime === startTime) {
                this.currentSync = null;
            }
        }
    }

    async syncUp() {
        const db = getDb();
        const syncItems = await db.syncItems.toCollection().toArray();
        if (!syncItems.length) {
            return;
        }
        this.updateSyncState({ isInProgress: true, setLengthUp: syncItems.length });
        let orphansRemoved = 0;
        // Each command keeps its own sync item: an item that cannot be built leaves no gap in the pairing.
        const built = (
            await Promise.all(
                syncItems.map(async (si) => {
                    try {
                        return { syncItem: si, command: await this.syncItemToCommand(si) };
                    } catch (e: any) {
                        if (e instanceof SyncRowMissingError) {
                            // Nothing is left to upload.
                            await db.syncItems.delete(si.id);
                            orphansRemoved++;
                            return null;
                        }
                        // Stays queued and counted as pending; the download still runs.
                        console.error(e);
                        logError(e.toString(), 'dbsyncer/verylow/1');
                        return null;
                    }
                })
            )
        ).filter((b) => b !== null);
        const commands = built.map((b) => b.command);
        let remoteResponse: any = { data: {} };
        let validCommands: DbSyncerUpCommand[] = commands; // may be rewritten if some commands fail to start execution
        try {
            const commandResult = await this.executeSyncUpCommands(commands);
            remoteResponse = commandResult.result;
            validCommands = commandResult.commands;
        } catch (e: any) {
            if (isNoCurrentUserError(e)) {
                validCommands = [];
                initReauthFlow();
                return;
            }
            if (isNetworkError(e)) {
                validCommands = [];
                console.warn('DbSyncer: offline');
                return;
            }
            if (e?.errors) {
                remoteResponse = e;
            } else {
                validCommands = [];
                console.error(e);
                logError(e.toString(), 'dbsyncer/verylow/2');
                throw e;
            }
        } finally {
            if (this.dropped) {
                validCommands = [];
            }
            let successfulCommands = 0;
            await Promise.all(
                validCommands.map(async (command): Promise<void> => {
                    const { syncItem } = built[commands.indexOf(command)];
                    if (!db[syncItem.tableName]) {
                        const message = `Trying to finish syncing with non-existing table name ${syncItem.tableName}`;
                        console.error(message);
                        logError(message, 'dbsyncer/low/1');
                        return;
                    }
                    let returned = remoteResponse.data?.[command.mutationName];
                    if (!returned) {
                        if (remoteResponse.errors) {
                            // if the error is because local record has no _version information, attempt forcing the _version = 1
                            // because commonly this is caused when an update succeeds, but the returned data with version information
                            // is lost or discarded
                            const commandError = remoteResponse.errors.find(
                                (e: any) => e.path?.[0] === command.mutationName
                            );
                            const createNotReturned =
                                commandError?.errorType === 'ConditionalCheckFailedException' &&
                                !command.input._version;
                            const updateNotReturned =
                                commandError?.errorType === 'InternalFailure' && command.input._version;
                            if (createNotReturned || updateNotReturned) {
                                if (command.appendOnly) {
                                    // if append-only, the item already exists but is not readable. So assume the item was
                                    // returned so that it will be deleted/processed in transaction.
                                    returned = command.input;
                                    remoteResponse.errors.splice(remoteResponse.errors.indexOf(commandError), 1);
                                    successfulCommands++;
                                } else {
                                    const maybeResult = await this.attemptRetryWithVersion(command, syncItem);
                                    if (maybeResult) {
                                        returned = maybeResult;
                                        remoteResponse.errors.splice(remoteResponse.errors.indexOf(commandError), 1);
                                        successfulCommands++;
                                    }
                                    // otherwise an error will be thrown from remoteResponse.error
                                }
                            }
                        } else {
                            const message = `Command result not found ${command.mutationName}`;
                            logError(message, 'dbsyncer/low/2');
                            throw new Error(message);
                        }
                    }
                    if (!returned) {
                        // retry was unsuccessful
                        return;
                    }
                    await db.transaction('rw', [db[syncItem.tableName], db.syncItems], async () => {
                        if (command.localSnapshot) {
                            // A reply can describe an older state of the row than the device now holds: the row
                            // may have been changed or removed on the device while the mutation was in flight.
                            const table: Dexie.Table = db[syncItem.tableName];
                            const current = await table.get(syncItem.itemId);
                            if (current === undefined) {
                                await db.syncItems.delete(syncItem.id);
                                successfulCommands++;
                                this.updateSyncState({ isInProgress: true, lengthUpIncrement: -1 });

                                return;
                            }
                            if (!isEqual(omit(current, '_version'), omit(command.localSnapshot, '_version'))) {
                                await table.update(syncItem.itemId, {
                                    _version: Math.max(returned._version, current._version ?? 0),
                                });

                                return;
                            }
                        }
                        const localDataStructure = command.remoteToLocalTransform(returned);
                        const needsResync = command.getNeedsResync
                            ? command.getNeedsResync(returned, localDataStructure)
                            : false;
                        // A row that has to be sent again stays in the table: its sync item is kept, and the
                        // next sync builds the command from the stored row.
                        const putOrDeletePromise =
                            returned.deleted && !needsResync
                                ? db[syncItem.tableName].delete(returned.id)
                                : db[syncItem.tableName].put(localDataStructure);
                        const executionPromises = [putOrDeletePromise as Promise<void>];
                        if (!needsResync) {
                            executionPromises.push(db.syncItems.delete(syncItem.id));
                        }
                        await Promise.all(executionPromises);
                        if (needsResync) {
                            const message = `Item needs resync ${syncItem.tableName}; ${syncItem.itemId}`;
                            console.warn(message);
                            logError(message, 'dbsyncer/resync');
                        } else {
                            successfulCommands++;
                            this.updateSyncState({ isInProgress: true, lengthUpIncrement: -1 });
                        }
                    });
                })
            );
            // Items that failed to build are still pending.
            const lengthUp = Math.max(syncItems.length - orphansRemoved - successfulCommands, 0);
            if (!this.dropped) {
                this.updateSyncState({ isInProgress: false, setLengthUp: lengthUp });
            }
        }
        if (remoteResponse.errors?.length) {
            const messages = getErrorMessagesFromSyncResult(remoteResponse);
            logError(messages, 'dbsyncer/high');
            throw remoteResponse;
        }
    }

    async syncItemToCommand(syncItem: SyncItem, forceSetVersion?: number): Promise<DbSyncerUpCommand> {
        const db = getDb();
        const { tableName } = syncItem;
        const item = await db[tableName]?.get(syncItem.itemId);
        if (!item) {
            throw new SyncRowMissingError(
                `Error when syncing, object ${syncItem.itemId} no longer exist in table ${tableName}`
            );
        }
        if (forceSetVersion) {
            (item as any)._version = forceSetVersion;
        }
        const definition = dbSyncerUpDefintions.get(tableName);
        if (!definition) {
            throw new Error(`No syncer up-definition exists for table ${tableName}`);
        }
        return { ...definition(item), localSnapshot: item };
    }

    async executeSyncUpCommands(
        commands: DbSyncerUpCommand[]
    ): Promise<{ result: any; commands: DbSyncerUpCommand[] }> {
        const { query, parameters, validCommands } = await syncUpCommandsToMutation(commands, 'syncUp');
        const gqlo = graphqlOperation(query, parameters);
        if (validCommands.length) {
            const result = await API.graphql(gqlo);
            return { result, commands: validCommands };
        }
        // Nothing to send is not a failure: the items stay queued and the download still runs.
        return { result: { data: {} }, commands: [] };
    }

    async syncDown(force?: boolean) {
        if (!this.isStale() && !force) {
            console.info('sync is not stale enough');
            return;
        }
        const currentUserId = this.getCurrentUserId() || undefined;

        const dateStart = Date.now();
        const [subscriptionListToUse, lastSync] = this.shouldDoCompleteResync()
            ? [dbSyncerResyncSubscriptions, 0]
            : [dbSyncerUpdateSubscriptions, this.lastSyncTime];

        console.info(
            'Sync start, ref',
            new Date(this.lastSyncTime),
            'using ',
            subscriptionListToUse === dbSyncerResyncSubscriptions ? 'complete' : 'partial',
            'resync'
        );
        const additionalData: DbSyncSubscriptionAdditionalData = {
            currentUserId,
            isInitialSync: subscriptionListToUse === dbSyncerResyncSubscriptions,
        };
        let syncCancelled = false;
        // The server's time of the query, like DataStore: the device clock may be off.
        let serverStart: number | undefined;
        try {
            let queue: QueueItem[] = subscriptionListToUse.map((subscription) => ({
                subscription,
            }));
            const promiseList: Array<Promise<void>> = [];
            this.updateSyncState({ isInProgress: true, setLengthDown: queue.length });
            while (queue.length) {
                const queries = queue.map((queue) => queue.subscription.getQuery(queue.nextToken, additionalData));
                const query = `query Sync($lastSync: AWSTimestamp){${queries.join(',')}}`;
                if (this.shouldDoCompleteResync() && !additionalData.isInitialSync) {
                    // when we reach the threshold while in download loop, cancel the loop
                    syncCancelled = true;
                    console.warn('Partial sync cancelled due to timeout');
                    break;
                }
                const initialResult: any = await API.graphql(graphqlOperation(query, { lastSync }));
                if (this.dropped) {
                    return;
                }
                for (const queueItem of queue) {
                    const { nextToken, items, startedAt } = initialResult.data[queueItem.subscription.key];
                    if (typeof startedAt === 'number') {
                        serverStart = Math.min(serverStart ?? startedAt, startedAt);
                    }
                    if (items.length) {
                        promiseList.push(queueItem.subscription.onNewItems(items));
                    }
                    if (queueItem.subscription.nextTokenGetter) {
                        queueItem.nextToken = queueItem.subscription.nextTokenGetter(
                            initialResult.data[queueItem.subscription.key],
                            additionalData
                        );
                    } else {
                        queueItem.nextToken = nextToken || undefined;
                    }
                }
                queue = queue.filter((q) => q.nextToken);
                this.updateSyncState({ setLengthDown: queue.length });
            }
            await Promise.all(promiseList);
            console.info('Sync complete');
            if (!syncCancelled && !this.dropped) {
                this.setLastSyncTime(serverStart ?? dateStart, dateStart);
                this.updateSyncState({ syncedAt: dateStart });
            }
        } catch (e: any) {
            if (isNoCurrentUserError(e)) {
                initReauthFlow();
                return;
            }
            if (isNetworkError(e)) {
                console.warn('DbSyncer: offline');
                return;
            }
            console.error('Error in DbSyncer/Down: ', e);
            logError(getErrorMessagesFromSyncResult(e), 'dbsyncer/down');
        }
    }

    private async attemptRetryWithVersion(command: DbSyncerUpCommand, syncItem: SyncItem): Promise<any | false> {
        try {
            const query = `query getVersion {${command.queryName}(id:"${syncItem.itemId}") { _version}}`;
            const result = (await API.graphql(graphqlOperation(query))) as any;
            const resultData = result.data?.[command.queryName];
            if (!resultData) {
                const message = `Retry with force-version failed for ${syncItem.tableName} - no result for record`;
                console.error(message);
                logError(message, 'dbsyncer/retry/1');

                return false;
            }
            const existingVersion = resultData._version;
            const retryCommand = await this.syncItemToCommand(syncItem, existingVersion || 1);
            const retryResult = await this.executeSyncUpCommands([retryCommand]);
            const returned = retryResult.result.data?.[retryCommand.mutationName];
            if (returned) {
                console.warn(`Had to force-set version for ${syncItem.tableName} ${syncItem.itemId}`);
                return returned;
            }
            console.log(retryResult);
            const message = getErrorMessagesFromSyncResult(retryResult.result);
            console.error(`Retry with force-version failed for ${syncItem.tableName} ${syncItem.itemId}\n\n${message}`);
            console.error(retryResult, retryCommand);
            logError(message, 'dbsyncer/retry/2');
        } catch (e: any) {
            // log only, as this is a non-critical retry attempt
            if (e.errors) {
                const message = getErrorMessagesFromSyncResult(e);
                console.error(
                    `Retry with force-version failed for ${syncItem.tableName} ${syncItem.itemId}\n\n${message}`
                );
                logError(message, 'dbsyncer/retry-low');
            } else {
                console.error(e);
                logError(e.toString(), 'dbsyncer/retry-verylow');
            }
        }
        return false;
    }

    private setLastSyncTime(time: number, deviceTime: number) {
        this.lastSyncTime = time;
        this.lastSyncDeviceTime = deviceTime;
        window.localStorage.setItem(LOCAL_STORAGE_KEY_LAST_SYNC, this.lastSyncTime.toString());
        window.localStorage.setItem(LOCAL_STORAGE_KEY_LAST_SYNC_DEVICE, this.lastSyncDeviceTime.toString());
    }

    private getCurrentUserId(): string | null {
        const maybeUser = store.getState().cognitoUser;
        if (cognitoUserIsSignedIn(maybeUser)) {
            // sync down the impersonated user's data, using the admin's own tokens
            return getImpersonatedCognitoState()?.id ?? maybeUser.id;
        }
        return null;
    }
}

let dbSyncer: DbSyncer | null;

export const getDbSyncer = (): DbSyncer => {
    if (!dbSyncer) {
        dbSyncer = new DbSyncer();
    }
    return dbSyncer;
};

// The syncer belongs to the signed-out session: the next sign-in gets a new one, whose first sync -
// awaited by the sign-in flow (see tryCreateTlbUser) - runs for the new user against the wiped database.
export const wipeDbSyncer = () => {
    window.localStorage.setItem(LOCAL_STORAGE_KEY_LAST_SYNC, '0');
    if (dbSyncer) {
        dbSyncer.dropped = true;
        window.clearInterval(dbSyncer.syncInterval ?? undefined);
        window.clearTimeout(dbSyncer.timeout ?? undefined);
        dbSyncer = null;
    }
};

const getErrorMessagesFromSyncResult = function (syncResult: any): string {
    if (syncResult.errors) {
        return syncResult.errors.map((e: any) => `${e.message}@${e.path}`).join('\n----\n');
    } else if (typeof syncResult === 'object') {
        try {
            return JSON.stringify(syncResult);
        } catch {
            return syncResult.toString();
        }
    } else {
        return syncResult.toString();
    }
};
